import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Icon, { type IconName } from '../components/Brand/Icon';
import { usePageTitle } from '../hooks/usePageTitle';
import ui from '../styles/ui.module.css';
import styles from './SecurityPage.module.css';

// The security page: what we can and can't see, what protects a secret, how
// to check the code, the limits, who runs it, and how to report a problem. Every claim
// here is backed by docs/threat-model.md, docs/crypto-spec.md or an ADR;
// change them together.

// Bump when the text changes materially.
const LAST_UPDATED = '2026-11-04';

const NEVER = ['content', 'key', 'passphrase', 'card'] as const;
const SEEN = ['payload', 'details', 'receipt', 'live', 'ip', 'pass'] as const;

const PROTECTIONS: [key: string, icon: IconName][] = [
  ['encrypt', 'lock'],
  ['key', 'key'],
  ['passphrase', 'shield'],
  ['burn', 'flame'],
  ['live', 'people'],
  ['probe', 'notStored'],
];

const CODE_FACTS = ['scripts', 'csp', 'turnstile', 'headers'] as const;
const LIMITS = ['page', 'device', 'recipient', 'firstOpen', 'metadata', 'direct', 'password'] as const;
const RUNS = ['hosting', 'stripe', 'us', 'legal'] as const;

export default function SecurityPage() {
  const { t } = useTranslation();
  usePageTitle(t('security.title'));
  const email = t('security.report.email');

  return (
    <main className={ui.page}>
      <header className={styles.intro}>
        <h1 className={styles.title}>{t('security.title')}</h1>
        <p className={styles.lead}>{t('security.lead')}</p>
        <p className={ui.hint}>{t('legal.lastUpdated', { date: LAST_UPDATED })}</p>
      </header>

      <section className={styles.section} aria-labelledby="sees-title">
        <h2 id="sees-title" className={styles.sectionTitle}>
          {t('security.sees.title')}
        </h2>
        <div className={styles.sees}>
          <div className={`${styles.seesCard} ${styles.never}`}>
            <h3 className={styles.seesTitle}>{t('security.sees.never.title')}</h3>
            <ul className={styles.list}>
              {NEVER.map((item) => (
                <li key={item}>
                  <Icon name="lock" size={18} strokeWidth={2} className={styles.listIcon} />
                  <span>{t(`security.sees.never.${item}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.seesCard}>
            <h3 className={styles.seesTitle}>{t('security.sees.seen.title')}</h3>
            <ul className={styles.list}>
              {SEEN.map((item) => (
                <li key={item}>
                  <span className={styles.dot} aria-hidden="true" />
                  <span>{t(`security.sees.seen.${item}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="protections-title">
        <h2 id="protections-title" className={styles.sectionTitle}>
          {t('security.protections.title')}
        </h2>
        <ul className={styles.cards}>
          {PROTECTIONS.map(([key, icon]) => (
            <li key={key} className={styles.card}>
              <span className={styles.cardIcon} aria-hidden="true">
                <Icon name={icon} size={22} />
              </span>
              <h3 className={styles.cardTitle}>{t(`security.protections.${key}.title`)}</h3>
              <p>{t(`security.protections.${key}.body`)}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="code-title">
        <h2 id="code-title" className={styles.sectionTitle}>
          {t('security.code.title')}
        </h2>
        <p className={styles.sectionLead}>{t('security.code.lead')}</p>
        <ul className={styles.checks}>
          <li>
            <Icon name="code" size={20} strokeWidth={2} className={styles.listIcon} />
            <span>
              {t('security.code.files')}{' '}
              <Link to="/how-it-works#code" className={styles.strongLink}>
                {t('security.code.filesLink')} <span aria-hidden="true">→</span>
              </Link>
            </span>
          </li>
          {CODE_FACTS.map((fact) => (
            <li key={fact}>
              <Icon name="shield" size={20} strokeWidth={2} className={styles.listIcon} />
              <span>{t(`security.code.${fact}`)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="limits-title">
        <h2 id="limits-title" className={styles.sectionTitle}>
          {t('security.limits.title')}
        </h2>
        <p className={styles.sectionLead}>{t('security.limits.lead')}</p>
        <dl className={styles.limits}>
          {LIMITS.map((limit) => (
            <div key={limit} className={styles.limit}>
              <dt>{t(`security.limits.${limit}.title`)}</dt>
              <dd>{t(`security.limits.${limit}.body`)}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className={styles.section} aria-labelledby="runs-title">
        <h2 id="runs-title" className={styles.sectionTitle}>
          {t('security.runs.title')}
        </h2>
        <dl className={styles.runs}>
          {RUNS.map((who) => (
            <div key={who} className={styles.run}>
              <dt>{t(`security.runs.${who}.title`)}</dt>
              <dd>{t(`security.runs.${who}.body`)}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className={`${styles.section} ${styles.report}`} aria-labelledby="report-title">
        <h2 id="report-title" className={styles.sectionTitle}>
          {t('security.report.title')}
        </h2>
        <p className={styles.reportLead}>{t('security.report.lead')}</p>
        <p>
          <a href={`mailto:${email}`} className={styles.email}>
            {/* On a phone, break after the @ rather than mid-word. */}
            {email.split('@')[0]}@<wbr />
            {email.split('@')[1]}
          </a>
        </p>
        <ul className={styles.reportList}>
          <li>{t('security.report.ack')}</li>
          <li>{t('security.report.safeHarbor')}</li>
          <li>{t('security.report.rules')}</li>
          <li>
            <a href="/.well-known/security.txt">{t('security.report.securityTxt')}</a>
          </li>
        </ul>
      </section>
    </main>
  );
}
